import { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

// Fallbacks match ui/paradex/src/assets/base.css dark-theme values. The
// desktop app does not declare --text-primary / --text-secondary globally
// (existing terminal styles already rely on inherit), so the gradient must
// resolve even with the vars undefined or webkit-text-fill-color: transparent
// hides the text entirely.
export const ProcessingLabel = styled.span`
  background: linear-gradient(
    90deg,
    var(--text-secondary, #b9bbbd) 0%,
    var(--text-secondary, #b9bbbd) 35%,
    var(--text-primary, #e8e8e8) 50%,
    var(--text-secondary, #b9bbbd) 65%,
    var(--text-secondary, #b9bbbd) 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: ${shimmer} 2.5s linear infinite;

  /* Windows high-contrast / forced-colors overrides the gradient but keeps
     the transparent text fills, which would hide the indicator entirely.
     Restore a system-aware solid color so the label stays visible. */
  @media (forced-colors: active) {
    color: CanvasText;
    -webkit-text-fill-color: currentColor;
    background: none;
    animation: none;
  }
`;

export const ProcessingDetail = styled.span`
  color: var(--text-secondary, #b9bbbd);
  opacity: 0.75;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 40ch;
`;

export const ProcessingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary, #b9bbbd);
  font-size: 11px;
  padding: 4px 8px;
`;

export const THINKING_WORDS = [
  'Pondering',
  'Musing',
  'Reflecting',
  'Reasoning',
  'Contemplating',
  'Cogitating',
  'Deliberating',
  'Considering',
  'Ruminating',
  'Brainstorming',
  'Processing',
  'Analyzing',
  'Untangling',
  'Computing',
  'Crunching',
  'Parsing',
  'Synthesizing',
  'Evaluating',
  'Investigating',
  'Decoding',
] as const;

// `sendMessage` seeds `processing: { label: 'Thinking' }` as a placeholder
// before the gateway echoes a concrete state. Treat that literal value as
// "no specific state yet — cycle the shimmer verbs". Concrete labels from
// PROCESSING_STATE_LABELS ('Calling tool', 'Searching', 'Planning',
// 'Writing', …) override the cycle so the user can tell what the agent is
// actually doing.
const GENERIC_THINKING_LABEL = 'Thinking';

interface CyclingThinkingLabelProps {
  readonly label?: string | null;
}

export function CyclingThinkingLabel({ label }: CyclingThinkingLabelProps) {
  const hasConcreteLabel =
    label != null && label.length > 0 && label !== GENERIC_THINKING_LABEL;

  // Index only matters while cycling. Start at 0 (deterministic) so a quick
  // hide/show of the indicator doesn't visually jump to a random new word —
  // the regression the random initializer caused.
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (hasConcreteLabel) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % THINKING_WORDS.length);
    }, 2500);
    return () => window.clearInterval(id);
  }, [hasConcreteLabel]);

  const text = hasConcreteLabel ? label : `${THINKING_WORDS[index]}...`;

  // The cycling verbs are decorative — they exist to signal "agent is
  // working" without naming what it is doing. Hide them from screen readers
  // so a polite live region doesn't announce "Pondering... Musing..." every
  // 2.5s. Concrete labels (Calling tool, Searching, …) remain announceable.
  return (
    <ProcessingLabel aria-hidden={!hasConcreteLabel ? true : undefined}>
      {text}
    </ProcessingLabel>
  );
}
